import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Divider,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { OVERLAY_NAMES } from "constants/Overlays";
import { useStateContext } from "contexts/ContextProvider";
import { useNavigate } from "react-router-dom";
import api from "services/api";

const CATEGORY_ORDER = ["clients", "linguists", "services", "invoices", "expenses"];
const CATEGORY_LABELS = {
  clients: "Clients",
  linguists: "Linguists",
  services: "Services",
  invoices: "Invoices",
  expenses: "Expenses",
};

const CATEGORY_FILTERS = [
  { key: "clients", label: "Client", prefix: "client:" },
  { key: "linguists", label: "Linguist", prefix: "linguist:" },
  { key: "services", label: "Service", prefix: "service:" },
  { key: "invoices", label: "Invoice", prefix: "invoice:" },
  { key: "expenses", label: "Expense", prefix: "expense:" },
];

const CATEGORY_PREFIX_ALIASES = {
  client: "clients",
  clients: "clients",
  linguist: "linguists",
  linguists: "linguists",
  service: "services",
  services: "services",
  invoice: "invoices",
  invoices: "invoices",
  expense: "expenses",
  expenses: "expenses",
};

const getCategoryColor = (categoryKey, theme) =>
  categoryKey === "clients"
    ? theme.palette.primary.main
    : categoryKey === "linguists"
    ? theme.palette.secondary.main
    : categoryKey === "services"
    ? theme.palette.info.main
    : categoryKey === "invoices"
    ? theme.palette.success.main
    : theme.palette.warning.main;

const canSearch = (value) => String(value || "").trim().length >= 2;
const QUICK_CATEGORY_DISPLAY_LIMIT = 5;
const RECENT_SEARCH_LIMIT = 10;

const getRecentSearchStorageKey = (userId) =>
  `ce_portal.global_search.recent.${String(userId || "anonymous")}`;

const parseCategoryFilterFromQuery = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^([a-zA-Z]+)\s*:\s*(.*)$/);

  if (!match) {
    return {
      rawQuery: raw,
      cleanQuery: raw,
      category: "",
      hasFilter: false,
    };
  }

  const alias = String(match[1] || "").toLowerCase();
  const category = CATEGORY_PREFIX_ALIASES[alias] || "";
  const cleanQuery = String(match[2] || "").trim();

  if (!category) {
    return {
      rawQuery: raw,
      cleanQuery: raw,
      category: "",
      hasFilter: false,
    };
  }

  return {
    rawQuery: raw,
    cleanQuery,
    category,
    hasFilter: true,
  };
};

const getQueryTerms = (value) => {
  const terms = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((term) => term.toLowerCase());

  return Array.from(new Set(terms));
};

const getQueryTermsWithOriginalCase = (value) => {
  const rawTerms = String(value || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const seen = new Set();
  const deduped = [];

  rawTerms.forEach((term) => {
    const normalized = term.toLowerCase();
    if (seen.has(normalized)) return;
    seen.add(normalized);
    deduped.push({ normalized, original: term });
  });

  return deduped;
};

const getMatchedTokenCount = (item, queryTerms) => {
  if (!Array.isArray(queryTerms) || !queryTerms.length) return 0;

  const matchedTokens = Array.isArray(item?.matchedTokens)
    ? item.matchedTokens.map((token) => String(token || "").toLowerCase())
    : [];

  if (!matchedTokens.length) return 0;
  return queryTerms.filter((term) => matchedTokens.includes(term)).length;
};

const getItemTokenSet = (item) =>
  new Set(
    (Array.isArray(item?.matchedTokens) ? item.matchedTokens : []).map((token) =>
      String(token || "").toLowerCase(),
    ),
  );

const buildDropdownGroups = (items, queryValue, queryTerms, queryTermsWithCase = []) => {
  const normalizedTerms = Array.isArray(queryTerms)
    ? queryTerms.map((term) => String(term || "").toLowerCase())
    : [];

  const sortedItems = [...(Array.isArray(items) ? items : [])].sort((a, b) => {
    const aCount = getMatchedTokenCount(a, normalizedTerms);
    const bCount = getMatchedTokenCount(b, normalizedTerms);
    if (aCount !== bCount) return bCount - aCount;
    return 0;
  });

  if (normalizedTerms.length < 2) {
    return [
      {
        key: "default",
        title: "",
        items: sortedItems.slice(0, QUICK_CATEGORY_DISPLAY_LIMIT),
      },
    ];
  }

  const groups = [];
  const consumedIds = new Set();

  const bothItems = sortedItems.filter((item) => {
    const tokens = getItemTokenSet(item);
    return normalizedTerms.every((term) => tokens.has(term));
  });

  if (bothItems.length) {
    groups.push({
      key: "all-terms",
      title: `Showing results for '${queryValue}'`,
      items: bothItems,
    });

    bothItems.forEach((item) => consumedIds.add(`${item.entityType || "Unknown"}-${item.id}`));
  }

  normalizedTerms.forEach((term) => {
    const termItems = sortedItems.filter((item) => {
      const idKey = `${item.entityType || "Unknown"}-${item.id}`;
      if (consumedIds.has(idKey)) return false;
      const tokens = getItemTokenSet(item);
      return tokens.has(term);
    });

    if (!termItems.length) return;

    const displayTerm =
      queryTermsWithCase.find((entry) => entry.normalized === term)?.original || term;

    groups.push({
      key: `term-${term}`,
      title: `Showing results for '${displayTerm}'`,
      items: termItems,
    });

    termItems.forEach((item) => consumedIds.add(`${item.entityType || "Unknown"}-${item.id}`));
  });

  return groups;
};

const GlobalSearchBar = ({ user }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { setOverlayData, setOverlayName } = useStateContext();
  const anchorRef = useRef(null);
  const inputRef = useRef(null);

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [searchError, setSearchError] = useState("");
  const [open, setOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const itemRefs = useRef([]);

  const isAdmin = String(user?.Role || "").toLowerCase() === "admin";
  const parsedQuery = useMemo(() => parseCategoryFilterFromQuery(query), [query]);
  const isExpanded = isHovered || open || Boolean(query.trim());

  useEffect(() => {
    const storageKey = getRecentSearchStorageKey(user?.id);

    try {
      const parsed = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
      const normalized = Array.isArray(parsed)
        ? parsed
            .map((item) => String(item || "").trim())
            .filter((item) => item.length > 0)
            .slice(0, RECENT_SEARCH_LIMIT)
        : [];

      setRecentSearches(normalized);
    } catch (error) {
      setRecentSearches([]);
    }
  }, [user?.id]);

  const persistRecentSearches = useCallback(
    (nextItems) => {
      const normalized = Array.isArray(nextItems)
        ? nextItems
            .map((item) => String(item || "").trim())
            .filter((item) => item.length > 0)
            .slice(0, RECENT_SEARCH_LIMIT)
        : [];

      setRecentSearches(normalized);

      try {
        window.localStorage.setItem(
          getRecentSearchStorageKey(user?.id),
          JSON.stringify(normalized),
        );
      } catch (error) {
        // Ignore localStorage failures (private mode/quota).
      }
    },
    [user?.id],
  );

  const addRecentSearch = useCallback(
    (rawValue) => {
      const nextValue = String(rawValue || "").trim();
      const parsed = parseCategoryFilterFromQuery(nextValue);
      if (!canSearch(parsed.cleanQuery)) return;

      const deduped = [
        nextValue,
        ...recentSearches.filter(
          (item) => String(item || "").toLowerCase() !== nextValue.toLowerCase(),
        ),
      ].slice(0, RECENT_SEARCH_LIMIT);

      persistRecentSearches(deduped);
    },
    [persistRecentSearches, recentSearches],
  );

  const handleSelectRecentSearch = useCallback((value) => {
    setQuery(value);
    setOpen(true);
    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);

  const handleRemoveRecentSearch = useCallback(
    (valueToRemove) => {
      const nextItems = recentSearches.filter(
        (item) => String(item || "").toLowerCase() !== String(valueToRemove || "").toLowerCase(),
      );
      persistRecentSearches(nextItems);
    },
    [persistRecentSearches, recentSearches],
  );

  const handleClearRecentSearches = useCallback(() => {
    persistRecentSearches([]);
  }, [persistRecentSearches]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [query]);

  const visibleCategories = useMemo(() => {
    const base = isAdmin
      ? CATEGORY_ORDER
      : CATEGORY_ORDER.filter((category) => category !== "invoices" && category !== "expenses");

    if (parsedQuery.category && base.includes(parsedQuery.category)) {
      return [parsedQuery.category];
    }

    return base;
  }, [isAdmin, parsedQuery.category]);

  const sortedVisibleCategories = useMemo(() => {
    const queryTerms = getQueryTerms(parsedQuery.cleanQuery);

    if (queryTerms.length < 2) {
      return visibleCategories;
    }

    const hasExactLinguist = (results?.categories?.linguists?.items || []).some(
      (item) => Boolean(item?.exactNameMatch),
    );
    const hasExactClient = (results?.categories?.clients?.items || []).some(
      (item) => Boolean(item?.exactNameMatch),
    );
    const hasExactServicePair = (results?.categories?.services?.items || []).some(
      (item) => Boolean(item?.exactPairMatch),
    );

    const strictPriority = {
      linguists: hasExactLinguist ? 0 : 10,
      clients: hasExactClient ? 1 : 10,
      services: hasExactServicePair ? 2 : 10,
    };

    const getCategoryTier = (categoryKey) => {
      const items = Array.isArray(results?.categories?.[categoryKey]?.items)
        ? results.categories[categoryKey].items
        : [];

      if (!items.length) return 2;

      const hasAllTerms = items.some((item) => {
        const matchedTokens = Array.isArray(item?.matchedTokens)
          ? item.matchedTokens.map((token) => String(token || "").toLowerCase())
          : [];
        return queryTerms.every((term) => matchedTokens.includes(term));
      });

      if (hasAllTerms) return 0;

      const hasSingleTerm = items.some((item) => {
        const matchedTokens = Array.isArray(item?.matchedTokens)
          ? item.matchedTokens.map((token) => String(token || "").toLowerCase())
          : [];
        return queryTerms.some((term) => matchedTokens.includes(term));
      });

      return hasSingleTerm ? 1 : 2;
    };

    return [...visibleCategories].sort((a, b) => {
      const aStrict = strictPriority[a] ?? 10;
      const bStrict = strictPriority[b] ?? 10;
      if (aStrict !== bStrict) return aStrict - bStrict;

      const aTier = getCategoryTier(a);
      const bTier = getCategoryTier(b);
      if (aTier !== bTier) return aTier - bTier;
      return CATEGORY_ORDER.indexOf(a) - CATEGORY_ORDER.indexOf(b);
    });
  }, [parsedQuery.cleanQuery, results, visibleCategories]);

  const queryTerms = useMemo(() => getQueryTerms(parsedQuery.cleanQuery), [parsedQuery.cleanQuery]);
  const queryTermsWithCase = useMemo(
    () => getQueryTermsWithOriginalCase(parsedQuery.cleanQuery),
    [parsedQuery.cleanQuery],
  );

  const categoryModels = useMemo(() => {
    if (!canSearch(parsedQuery.cleanQuery)) return [];

    let flatIndex = 0;

    return sortedVisibleCategories
      .map((categoryKey) => {
        const categoryData = results?.categories?.[categoryKey] || { items: [], total: 0 };
        const groups = buildDropdownGroups(
          categoryData.items,
          parsedQuery.cleanQuery,
          queryTerms,
          queryTermsWithCase,
        );
        const items = groups.flatMap((group) => group.items);

        const itemIndexMap = new Map();
        groups.forEach((group) => {
          group.items.forEach((item) => {
            itemIndexMap.set(`${categoryKey}-${group.key}-${item.id}`, flatIndex);
            flatIndex += 1;
          });
        });

        return { categoryKey, categoryData, groups, items, itemIndexMap };
      })
      .filter((model) => model.items.length > 0);
  }, [parsedQuery.cleanQuery, queryTerms, queryTermsWithCase, results, sortedVisibleCategories]);

  const navigableItems = useMemo(() => {
    if (!canSearch(parsedQuery.cleanQuery)) {
      return recentSearches.map((value) => ({ type: "recent", value }));
    }

    const items = [];
    categoryModels.forEach((model) => {
      model.groups.forEach((group) => {
        group.items.forEach((item) => {
          items.push({ type: "result", item, categoryKey: model.categoryKey });
        });
      });
    });

    return items;
  }, [categoryModels, parsedQuery.cleanQuery, recentSearches]);

  const fetchSearchResults = useCallback(async (term) => {
    const parsedTerm = parseCategoryFilterFromQuery(term);
    const termToSearch = parsedTerm.cleanQuery;

    if (!canSearch(termToSearch)) {
      setResults(null);
      setSearchError("");
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await api.get("/search/global", {
        params: {
          q: termToSearch,
          category: parsedTerm.category || undefined,
          limit: 40,
          offset: 0,
        },
      });

      setResults(response.data || null);
      const errorMap = response?.data?.errors || {};
      const hasErrors = Object.keys(errorMap).length > 0;
      setSearchError(hasErrors ? "Some categories could not be searched right now." : "");
    } catch (error) {
      console.error("Failed to fetch global search results", error);
      setResults({ categories: {} });
      setSearchError("Search request failed. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSearchResults(debouncedQuery);
  }, [debouncedQuery, fetchSearchResults]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [navigableItems.length, open]);

  useEffect(() => {
    if (activeIndex < 0) return;
    itemRefs.current[activeIndex]?.scrollIntoView?.({ block: "nearest" });
  }, [activeIndex]);

  const handleOpenResults = () => {
    setOpen(true);
  };

  const handleCloseResults = () => {
    setOpen(false);
  };

  const hasAnyResults = useMemo(() => {
    if (!results?.categories) return false;
    return visibleCategories.some((category) => (results.categories?.[category]?.items || []).length > 0);
  }, [results, visibleCategories]);

  const clearSearchState = useCallback(() => {
    setQuery("");
    setDebouncedQuery("");
    setResults(null);
    setSearchError("");
  }, []);

  const handleNavigateResult = (item) => {
    if (!item?.routePath) return;

    addRecentSearch(query);

    clearSearchState();
    setOpen(false);
    navigate(item.routePath);
  };

  const handleShowMore = (categoryKey) => {
    const nextQuery = parsedQuery.cleanQuery;
    if (!nextQuery) return;

    addRecentSearch(query);

    clearSearchState();
    setOpen(false);
    setOverlayData({
      query: nextQuery,
      category: parsedQuery.category || categoryKey,
    });
    setOverlayName(OVERLAY_NAMES.GLOBAL_SEARCH);
  };

  const handleApplyCategoryChip = (categoryKey) => {
    const filterConfig = CATEGORY_FILTERS.find((item) => item.key === categoryKey);
    if (!filterConfig) return;

    const nextValue = parsedQuery.cleanQuery
      ? `${filterConfig.prefix} ${parsedQuery.cleanQuery}`
      : `${filterConfig.prefix} `;

    setQuery(nextValue);
    setOpen(true);
    window.setTimeout(() => {
      inputRef.current?.focus();
      const valueLength = String(nextValue || "").length;
      inputRef.current?.setSelectionRange?.(valueLength, valueLength);
    }, 0);
  };

  const handleClearQuery = () => {
    setQuery("");
    setDebouncedQuery("");
    setResults(null);
    setSearchError("");
    setOpen(true);

    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  const handleCollapsedActivate = () => {
    setIsHovered(true);
    setOpen(true);
    window.setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  };

  return (
    <ClickAwayListener onClickAway={handleCloseResults}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", flexShrink: 0 }}>
      <Box
        ref={anchorRef}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        sx={{
          width: isExpanded ? { xs: 180, sm: 240, md: 300 } : 38,
          minWidth: 38,
          transition: "width 220ms cubic-bezier(0.2, 0, 0, 1)",
          overflow: "visible",
        }}
      >
        {isExpanded ? (
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            value={query}
            placeholder="Search anything in portal..."
            onChange={(event) => {
              setQuery(event.target.value);
              if (!open) setOpen(true);
            }}
            onFocus={handleOpenResults}
            onKeyDown={(event) => {
              if (event.key === "Escape") {
                handleCloseResults();
                return;
              }

              if (event.key === "ArrowDown") {
                event.preventDefault();
                if (!navigableItems.length) return;
                if (!open) setOpen(true);
                setActiveIndex((current) => (current + 1) % navigableItems.length);
                return;
              }

              if (event.key === "ArrowUp") {
                event.preventDefault();
                if (!navigableItems.length) return;
                if (!open) setOpen(true);
                setActiveIndex((current) =>
                  current <= 0 ? navigableItems.length - 1 : current - 1,
                );
                return;
              }

              if (event.key === "Enter") {
                if (activeIndex >= 0 && navigableItems[activeIndex]) {
                  event.preventDefault();
                  const target = navigableItems[activeIndex];
                  if (target.type === "recent") {
                    handleSelectRecentSearch(target.value);
                  } else {
                    handleNavigateResult(target.item);
                  }
                  return;
                }

                const cleanQuery = parsedQuery.cleanQuery;
                if (!canSearch(cleanQuery)) return;

                event.preventDefault();
                addRecentSearch(query);
                clearSearchState();
                setOpen(false);
                setOverlayData({
                  query: cleanQuery,
                  category: parsedQuery.category || undefined,
                });
                setOverlayName(OVERLAY_NAMES.GLOBAL_SEARCH);
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" sx={{ color: theme.palette.primary.main }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  {loading ? <CircularProgress size={16} /> : null}
                  {query ? (
                    <IconButton
                      aria-label="Clear search"
                      size="small"
                      edge="end"
                      onClick={handleClearQuery}
                      sx={{ ml: loading ? 0.5 : 0 }}
                    >
                      <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                  ) : null}
                </InputAdornment>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                backgroundColor: theme.palette.background.default,
                boxShadow: theme.shadows[3],
                minHeight: 38,
                height: 38,
                transition: "all 220ms ease",
                cursor: "text",
                px: 0.75,
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              },
            }}
          />
        ) : (
          <IconButton
            aria-label="Open search"
            onClick={handleCollapsedActivate}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handleCollapsedActivate();
              }
            }}
            sx={{
              height: "38px",
              width: "38px",
              minWidth: "38px",
              borderRadius: "12px",
              color: theme.palette.primary.main,
              bgcolor: theme.palette.background.default,
              boxShadow: theme.shadows[3],
              p: "7px",
              "&:hover": {
                bgcolor: theme.palette.action.hover,
              },
            }}
          >
            <SearchRoundedIcon />
          </IconButton>
        )}
      </Box>

      <Popper
        open={open && Boolean(anchorRef.current)}
        anchorEl={anchorRef.current}
        placement="bottom"
        sx={{
          zIndex: (currentTheme) => currentTheme.zIndex.modal + 1,
          width: { xs: "calc(100vw - 24px)", sm: 560 },
        }}
      >
        <Paper elevation={6} sx={{ p: 1.25, mt: 1, maxHeight: "68vh", borderRadius: 2, overflow: "auto" }}>
          {searchError && (
            <Box sx={{ px: 1.5, py: 1 }}>
              <Typography variant="caption" color="error.main">
                {searchError}
              </Typography>
            </Box>
          )}

          {!canSearch(parsedQuery.cleanQuery) ? (
            <Box sx={{ px: 1.5, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Type at least 2 characters to search across clients, linguists, services, notes, invoices, and expenses.
              </Typography>
              <Typography
                variant="caption"
                sx={{ mt: 1, display: "block", fontWeight: 600, color: theme.palette.text.secondary }}
              >
                Quick filter: click a chip to search only that category (for example, client: or linguist:).
              </Typography>
              <Box sx={{ mt: 1.25, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
                {CATEGORY_FILTERS.filter((item) => isAdmin || (item.key !== "invoices" && item.key !== "expenses")).map((item) => (
                  <Chip
                    key={item.key}
                    size="small"
                    label={item.label}
                    onClick={() => handleApplyCategoryChip(item.key)}
                    variant={parsedQuery.category === item.key ? "filled" : "outlined"}
                    sx={{
                      borderRadius: "8px",
                      fontWeight: 600,
                      color:
                        parsedQuery.category === item.key
                          ? theme.palette.common.white
                          : getCategoryColor(item.key, theme),
                      backgroundColor:
                        parsedQuery.category === item.key
                          ? getCategoryColor(item.key, theme)
                          : "transparent",
                      borderColor: getCategoryColor(item.key, theme),
                      "&:hover": {
                        backgroundColor:
                          parsedQuery.category === item.key
                            ? getCategoryColor(item.key, theme)
                            : theme.palette.action.hover,
                      },
                    }}
                  />
                ))}
              </Box>

              {recentSearches.length > 0 ? (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: theme.palette.text.secondary }}>
                      Recent Searches
                    </Typography>
                    <Button
                      size="small"
                      onClick={handleClearRecentSearches}
                      sx={{
                        minWidth: 0,
                        px: 1,
                        py: 0.25,
                        borderRadius: "8px",
                        textTransform: "none",
                        fontSize: theme.typography.caption.fontSize,
                        fontWeight: 700,
                        lineHeight: 1.2,
                      }}
                    >
                      Clear all
                    </Button>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {recentSearches.map((recentValue, index) => (
                      <ListItemButton
                        key={`recent-${recentValue}-${index}`}
                        selected={activeIndex === index}
                        ref={(element) => {
                          itemRefs.current[index] = element;
                        }}
                        onMouseEnter={() => setActiveIndex(index)}
                        onClick={() => handleSelectRecentSearch(recentValue)}
                        sx={{
                          px: 0.75,
                          py: 0.6,
                          borderRadius: "10px",
                          "&:hover": {
                            backgroundColor: theme.palette.action.hover,
                          },
                        }}
                      >
                        <HistoryRoundedIcon
                          fontSize="small"
                          sx={{ mr: 1, color: theme.palette.text.secondary }}
                        />
                        <ListItemText
                          primary={
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {recentValue}
                            </Typography>
                          }
                        />
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label="Remove recent search"
                          onClick={(event) => {
                            event.stopPropagation();
                            handleRemoveRecentSearch(recentValue);
                          }}
                        >
                          <DeleteOutlineRoundedIcon fontSize="small" />
                        </IconButton>
                      </ListItemButton>
                    ))}
                  </List>
                </>
              ) : null}
            </Box>
          ) : !loading && !hasAnyResults ? (
            <Box sx={{ px: 1.5, py: 2 }}>
              <Typography variant="body2" color="text.secondary">
                No results found.
              </Typography>
            </Box>
          ) : (
            categoryModels.map((model, categoryIndex) => {
              const { categoryKey, categoryData, groups, items } = model;

              return (
                <Box
                  key={categoryKey}
                  sx={{
                    mb: 0.75,
                    pt: categoryIndex === 0 ? 0 : 1.25,
                    borderTop: categoryIndex === 0 ? "none" : `1px solid ${theme.palette.divider}`,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      px: 0.5,
                      py: 0.75,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "999px",
                          backgroundColor:
                            categoryKey === "clients"
                              ? theme.palette.primary.main
                              : categoryKey === "linguists"
                              ? theme.palette.secondary.main
                              : categoryKey === "services"
                              ? theme.palette.info.main
                              : categoryKey === "invoices"
                              ? theme.palette.success.main
                              : theme.palette.warning.main,
                        }}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.08em", color: theme.palette.text.secondary }}>
                        {CATEGORY_LABELS[categoryKey]}
                      </Typography>
                      <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                        {categoryData.total || items.length} result{Number(categoryData.total || items.length) === 1 ? "" : "s"}
                      </Typography>
                    </Box>
                    {Number(categoryData.total || 0) > items.length && (
                      <Button
                        size="small"
                        onClick={() => handleShowMore(categoryKey)}
                        sx={{
                          minWidth: 0,
                          px: 1.25,
                          py: 0.25,
                          borderRadius: "10px",
                          textTransform: "none",
                          fontSize: theme.typography.caption.fontSize,
                          fontWeight: 700,
                          lineHeight: 1.2,
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.action.hover,
                        }}
                      >
                        Show more
                      </Button>
                    )}
                  </Box>

                  <Box>
                    {groups.map((group, groupIndex) => (
                      <Box key={`${categoryKey}-${group.key}`} sx={{ mb: groupIndex === groups.length - 1 ? 0 : 0.75 }}>
                        {group.title ? (
                          <Typography
                            variant="caption"
                            sx={{
                              display: "block",
                              px: 1,
                              pt: groupIndex === 0 ? 0 : 0.25,
                              pb: 0.35,
                              color: theme.palette.text.secondary,
                              fontWeight: 700,
                            }}
                          >
                            {group.title}
                          </Typography>
                        ) : null}

                        <List dense sx={{ py: 0, backgroundColor: theme.palette.background.paper }}>
                          {group.items.map((item, index) => {
                            const flatIndex = model.itemIndexMap.get(
                              `${categoryKey}-${group.key}-${item.id}`,
                            );

                            return (
                              <ListItemButton
                                key={`${categoryKey}-${group.key}-${item.id}`}
                                selected={activeIndex === flatIndex}
                                ref={(element) => {
                                  itemRefs.current[flatIndex] = element;
                                }}
                                onMouseEnter={() => setActiveIndex(flatIndex)}
                                onClick={() => handleNavigateResult(item)}
                                sx={{
                                  px: 0.75,
                                  py: 0.85,
                                  alignItems: "flex-start",
                                  borderTop:
                                    index === 0
                                      ? "none"
                                      : `1px dashed ${theme.palette.divider}`,
                                  borderRadius: "12px",
                                  gap: 1,
                                  "&:hover": {
                                    backgroundColor: theme.palette.action.hover,
                                  },
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 4,
                                    alignSelf: "stretch",
                                    borderRadius: "999px",
                                    backgroundColor:
                                      categoryKey === "clients"
                                        ? theme.palette.primary.main
                                        : categoryKey === "linguists"
                                        ? theme.palette.secondary.main
                                        : categoryKey === "services"
                                        ? theme.palette.info.main
                                        : categoryKey === "invoices"
                                        ? theme.palette.success.main
                                        : theme.palette.warning.main,
                                    opacity: 0.9,
                                  }}
                                />
                                <ListItemText
                                  primary={
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 700, mb: 0.25, lineHeight: 1.25 }}
                                    >
                                      {item.name || "Result"}
                                    </Typography>
                                  }
                                  secondary={
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      sx={{ display: "block", lineHeight: 1.5 }}
                                    >
                                      {item.subtitle || ""}
                                    </Typography>
                                  }
                                />
                              </ListItemButton>
                            );
                          })}
                        </List>
                      </Box>
                    ))}
                  </Box>
                </Box>
              );
            })
          )}
        </Paper>
      </Popper>
      </Box>
    </ClickAwayListener>
  );
};

export default GlobalSearchBar;
